import { NgTemplateOutlet } from '@angular/common';
import {
    afterNextRender,
    type ElementRef,
    Injector,
    inject,
    viewChild,
    viewChildren,
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    linkedSignal,
    output,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { NgIcon } from '@ng-icons/core';
import type { UserType } from '@quipex/shared/contracts';
import {
    QpxButton,
    QpxHeroIcon,
    QpxTypography,
    QpxIcon,
} from '@quipex/shared/ui';
import type { QuippySuggestedQuestion } from '../models';
import { getQuippyOutcomeSuggestions } from '../outcome-suggestions';
import { QuippySuggestedQuestions } from '../quippy-suggested-questions/quippy-suggested-questions';

import type { HomeSummaryLink } from './models';

type HomeAction = 'ask' | 'find' | 'pack';

@Component({
    selector: 'qpx-quippy-home-suggested-actions',
    imports: [
        NgIcon,
        NgTemplateOutlet,
        RouterLink,
        QpxIcon,
        QpxButton,
        QpxHeroIcon,
        QpxTypography,
        QuippySuggestedQuestions,
    ],
    templateUrl: './home-suggested-actions.html',
    styleUrl: './home-suggested-actions.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'block min-w-0',
        '[class.summary]': 'selected() === null',
    },
})
export class QuippyHomeSuggestedActions {
    public readonly questions =
        input.required<readonly QuippySuggestedQuestion[]>();
    public readonly scopeKind = input<'building' | 'portfolio'>('portfolio');
    public readonly userType = input<UserType>();
    public readonly packAvailable = input(false);
    public readonly secondaryActions = input<readonly HomeSummaryLink[]>([]);
    public readonly resetKey = input<string | null>(null);
    public readonly headingId = input('quippy-home-suggested-actions-title');
    public readonly questionSelected = output<QuippySuggestedQuestion>();

    private readonly injector = inject(Injector);
    private readonly backButton =
        viewChild<ElementRef<HTMLButtonElement>>('backButton');
    private readonly actionButtons =
        viewChildren<ElementRef<HTMLButtonElement>>('actionButton');

    protected selectAction(action: HomeAction): void {
        this.selected.set(action);
        afterNextRender(() => this.backButton()?.nativeElement.focus(), {
            injector: this.injector,
        });
    }

    protected backToSummary(): void {
        const index = this.cards().findIndex(
            (card) => card.id === this.selected()
        );
        this.selected.set(null);
        afterNextRender(
            () =>
                this.actionButtons()[Math.max(0, index)]?.nativeElement.focus(),
            { injector: this.injector }
        );
    }

    protected readonly selected = linkedSignal({
        source: () => ({
            scope: this.scopeKind(),
            userType: this.userType(),
            resetKey: this.resetKey(),
            packAvailable: this.packAvailable(),
        }),
        computation: (source, previous): HomeAction | null => {
            if (
                !previous ||
                source.scope !== previous.source.scope ||
                source.userType !== previous.source.userType ||
                source.resetKey !== previous.source.resetKey
            )
                return null;
            return previous.value === 'pack' && !source.packAvailable
                ? null
                : previous.value;
        },
    });
    protected readonly cards = computed(() => [
        {
            id: 'ask' as const,
            title: 'Ask a question',
            description:
                this.scopeKind() === 'building'
                    ? 'Understand your building'
                    : 'Understand your portfolio',
            icon: 'lucideLightbulb',
            context: 'primary' as const,
        },
        {
            id: 'find' as const,
            title: 'Find documents',
            description: 'Search files and records',
            icon: 'lucideFolder',
            context: 'info' as const,
        },
        ...(this.scopeKind() === 'building' && this.packAvailable()
            ? [
                  {
                      id: 'pack' as const,
                      title: 'Create a pack',
                      description: 'Organise useful documents',
                      icon: 'lucideNotebookTabs',
                      context: 'success' as const,
                  },
              ]
            : []),
    ]);
    protected readonly stackLinks = computed(
        () => this.cards().length === 3 && this.secondaryActions().length === 2
    );
    protected readonly detailTitle = computed(
        () =>
            this.cards().find((card) => card.id === this.selected())?.title ??
            ''
    );
    protected readonly detailQuestions = computed(() => {
        const action = this.selected();
        return action === 'find' || action === 'pack'
            ? getQuippyOutcomeSuggestions(
                  action,
                  this.scopeKind(),
                  this.userType()
              )
            : this.questions();
    });
}
